/**
 * Status taxonomy V2.
 * Runtime StatusEffectId remains frost|poison|burn|deepFreeze for M41 compatibility.
 * Semantic aliases: frost ≡ SLOW (CONTROL).
 */

import type { StatusEffectId } from '../../model/effects'

export type StatusCategory = 'CONTROL' | 'DOT' | 'DEBUFF'

/** Canonical V2 ids — ARMOR_BREAK is RESERVED (no Mage in M42). */
export type StatusEffectIdV2 =
  | 'SLOW'
  | 'POISON'
  | 'BURN'
  | 'DEEP_FREEZE'
  | 'ARMOR_BREAK'

export type StatusStackPolicy = 'REFRESH' | 'STACK' | 'REPLACE' | 'IGNORE'

export type StatusApplicationResultKind =
  | 'APPLIED'
  | 'REFRESHED'
  | 'IMMUNE'
  | 'RESISTED'
  | 'REJECTED'

export interface StatusEffectMeta {
  /** Runtime id used by GameSession / effects.ts */
  runtimeId: StatusEffectId | 'armorBreak'
  v2Id: StatusEffectIdV2
  categories: StatusCategory[]
  stackPolicy: StatusStackPolicy
}

/** ACTIVE status metadata including Armor Break (M46). */
export const STATUS_META: Record<StatusEffectIdV2, StatusEffectMeta> = {
  SLOW: {
    runtimeId: 'frost',
    v2Id: 'SLOW',
    categories: ['CONTROL'],
    stackPolicy: 'REFRESH'
  },
  POISON: {
    runtimeId: 'poison',
    v2Id: 'POISON',
    categories: ['DOT'],
    stackPolicy: 'REFRESH'
  },
  BURN: {
    runtimeId: 'burn',
    v2Id: 'BURN',
    categories: ['DOT'],
    stackPolicy: 'REFRESH'
  },
  DEEP_FREEZE: {
    runtimeId: 'deepFreeze',
    v2Id: 'DEEP_FREEZE',
    categories: ['CONTROL'],
    stackPolicy: 'REFRESH'
  },
  ARMOR_BREAK: {
    runtimeId: 'armorBreak',
    v2Id: 'ARMOR_BREAK',
    categories: ['DEBUFF'],
    stackPolicy: 'REFRESH'
  }
}

export function runtimeStatusToV2(id: StatusEffectId): StatusEffectIdV2 {
  switch (id) {
    case 'frost':
      return 'SLOW'
    case 'poison':
      return 'POISON'
    case 'burn':
      return 'BURN'
    case 'deepFreeze':
      return 'DEEP_FREEZE'
    case 'armorBreak':
      return 'ARMOR_BREAK'
  }
}

export function v2StatusToRuntime(id: StatusEffectIdV2): StatusEffectId | 'armorBreak' {
  return STATUS_META[id].runtimeId
}

export function statusCategories(id: StatusEffectId | StatusEffectIdV2): StatusCategory[] {
  if (id === 'frost' || id === 'SLOW') return STATUS_META.SLOW.categories
  if (id === 'poison' || id === 'POISON') return STATUS_META.POISON.categories
  if (id === 'burn' || id === 'BURN') return STATUS_META.BURN.categories
  if (id === 'deepFreeze' || id === 'DEEP_FREEZE') return STATUS_META.DEEP_FREEZE.categories
  if (id === 'ARMOR_BREAK' || id === 'armorBreak') return STATUS_META.ARMOR_BREAK.categories
  return []
}
